import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import { ArticleBody, DotList, Prose, Section } from "@/components/guide-page";
import { RiskBadge } from "@/components/risk-badge";
import { slugifyHeading, type TocItem } from "@/components/toc";
import { STATES } from "@/lib/states";
import { RISK_LEVELS, riskMeaning } from "@/lib/risk";
import { LEVEL_BASIS } from "@/lib/methodology";
import {
  CDC_STATUSES,
  CDC_STATUS_LABEL,
  CDC_STATUS_MEANING,
  CDC_STATUS_SOURCE,
  CDC_STATUS_VAR,
} from "@/lib/county-status-text";
import { SOURCES } from "@/lib/sources";
import { absUrl } from "@/lib/site";

const LEVELS_TITLE = "How we rate places";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About LoneStarMap: How We Rate Places and Check Sources" },
      {
        name: "description",
        content:
          "How LoneStarMap rates each state and county, where the data comes from, how sources are checked, and what the site does not do.",
      },
      { property: "og:title", content: "About LoneStarMap" },
      {
        property: "og:description",
        content: "How LoneStarMap rates each state and county, and how its sources are checked.",
      },
      { property: "og:url", content: absUrl("/about") },
    ],
    links: [{ rel: "canonical", href: absUrl("/about") }],
  }),
  component: AboutPage,
});

/** A source as a short link, for the few places this page names one. */
function SourceLink({ id }: { id: string }) {
  const ref = SOURCES[id];
  if (!ref) return null;
  return (
    <a href={ref.url} target="_blank" rel="noopener noreferrer" className="link" title={ref.title}>
      {ref.short ?? ref.org}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

function AboutPage() {
  const toc: TocItem[] = [
    "Why this exists",
    LEVELS_TITLE,
    "County pages",
    "Peak season",
    "How we check sources",
    "What this site does not do",
    "Corrections",
  ].map((label) => ({ id: slugifyHeading(label), label }));

  return (
    <PageShell
      title="About LoneStarMap"
      intro="LoneStarMap is a free field guide to the Lone Star tick and alpha-gal syndrome. It tells you whether the tick lives where you do, how to tell it apart, and what to do after a bite. This page explains how it decides what to tell you."
    >
      <ArticleBody toc={toc}>
        <Section id={slugifyHeading("Why this exists")} title="Why this exists">
          <Prose>
            Most people have never heard of alpha-gal syndrome, even where Lone Star ticks are
            common. People often find this site after a bite, when they are worried. So it puts the
            answer first, in plain words, and shows where each answer comes from.
          </Prose>
        </Section>

        <Section id={slugifyHeading(LEVELS_TITLE)} title={LEVELS_TITLE}>
          <Prose>
            Every state gets one of four levels. A level says where Lone Star ticks are found and,
            at the top level, where CDC has counted the most suspected alpha-gal cases. It is not
            your personal risk. A tick bite anywhere deserves the same care.
          </Prose>
          <ol className="mt-6 divide-y divide-border border-y border-border">
            {RISK_LEVELS.map((level) => {
              const places = STATES.filter((s) => s.risk === level).sort((a, b) =>
                a.name.localeCompare(b.name),
              );
              return (
                <li
                  key={level}
                  className="grid gap-x-8 gap-y-2 py-5 md:grid-cols-[11rem_minmax(0,1fr)]"
                >
                  <div>
                    <RiskBadge level={level} className="text-base" />
                    <p className="mt-1 text-sm text-muted-foreground">
                      {places.length} {places.length === 1 ? "place" : "places"}
                    </p>
                  </div>
                  <div className="min-w-0 space-y-2 text-[0.9375rem] leading-relaxed">
                    <p className="text-foreground">{riskMeaning[level]}</p>
                    <p className="text-muted-foreground">
                      <span className="font-semibold text-foreground">What it rests on:</span>{" "}
                      {LEVEL_BASIS[level]}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {places.map((s, i) => (
                        <span key={s.slug}>
                          <Link to="/states/$state" params={{ state: s.slug }} className="link">
                            {s.name}
                          </Link>
                          {i < places.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
          <Prose className="mt-6">
            The top and bottom levels follow fixed rules, and a test checks them against the data.
            &ldquo;Alpha-gal hotspot&rdquo; goes only to the 13 states named in{" "}
            <SourceLink id="cdc-ags-distribution" />. &ldquo;Rarely reported&rdquo; goes only to
            states where CDC lists no established county. Between &ldquo;Spreading&rdquo; and
            &ldquo;Found here&rdquo; there is no single cut-off number. We weigh CDC&rsquo;s county
            counts with the state&rsquo;s own sources, and each state page shows both.
          </Prose>
        </Section>

        <Section id={slugifyHeading("County pages")} title="County pages">
          <Prose>
            Each county page opens with CDC&rsquo;s own record for that county, from{" "}
            <SourceLink id={CDC_STATUS_SOURCE.sourceId} />. CDC sorts every county into one of three
            groups:
          </Prose>
          <ul className="mt-4 divide-y divide-border border-y border-border">
            {[...CDC_STATUSES].reverse().map((s) => (
              <li key={s} className="grid gap-x-8 gap-y-1 py-3 md:grid-cols-[11rem_minmax(0,1fr)]">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 ring-1 ring-foreground/25 [print-color-adjust:exact]"
                    style={{ backgroundColor: CDC_STATUS_VAR[s] }}
                  />
                  {CDC_STATUS_LABEL[s]}
                </span>
                <span className="text-[0.9375rem] leading-relaxed text-foreground">
                  {CDC_STATUS_MEANING[s]}
                </span>
              </li>
            ))}
          </ul>
          <Prose className="mt-4">
            We never invent a number for a county. Where CDC names a county for its alpha-gal cases,
            the page quotes CDC&rsquo;s figures and links the report.
          </Prose>
        </Section>

        <Section id={slugifyHeading("Peak season")} title="Peak season">
          <Prose>
            Each state&rsquo;s peak months come from a named source. Where a state has its own
            figure, we use it. Where it does not, we use a neighbouring state&rsquo;s figure or
            CDC&rsquo;s national guide, and the page says which.{" "}
            <Link to="/$guide" params={{ guide: "tick-season-by-state" }} className="link">
              See every state&rsquo;s months and sources
            </Link>
            .
          </Prose>
        </Section>

        <Section id={slugifyHeading("How we check sources")} title="How we check sources">
          <DotList
            items={[
              "Every fact on a page links to a source. The full list is on the sources page.",
              "We open each new source and find the sentence that supports the claim before it goes on a page.",
              "Medical facts come from CDC, state health departments, university extension programs and peer-reviewed research.",
              "Where public data is thin, the page says so instead of guessing.",
              "Reading pages show when they were last reviewed.",
            ]}
          />
          <Prose className="mt-4">
            <Link to="/sources" className="link">
              See every source
            </Link>
            .
          </Prose>
        </Section>

        <Section
          id={slugifyHeading("What this site does not do")}
          title="What this site does not do"
        >
          <DotList
            items={[
              "It does not give a personal risk score. Levels describe places, not people.",
              "It does not diagnose or treat anything. Only a clinician can diagnose alpha-gal syndrome or a tick-borne illness.",
              "It does not collect tick reports. Official surveillance runs through state health departments.",
              "The bite log stays on your device. It is never uploaded.",
            ]}
          />
        </Section>

        <Section id={slugifyHeading("Corrections")} title="Corrections">
          <Prose>
            If something here is wrong or out of date, please{" "}
            <Link to="/contact" className="link">
              tell us
            </Link>
            , with a link to the source if you have one. LoneStarMap is not medical advice. Read the{" "}
            <Link to="/disclaimer" className="link">
              disclaimer
            </Link>
            .
          </Prose>
        </Section>
      </ArticleBody>
    </PageShell>
  );
}
